import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";
import toast from "react-hot-toast";
import { Plus, Users, Trash2, Pencil, X, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const emptyCustomer = { name: "", company: "", phone: "", email: "", gst: "", address: "", city: "", state: "", state_code: "", country: "India", pincode: "" };

export default function CustomersPage() {
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState("");
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(emptyCustomer);
  const [editId, setEditId] = useState(null);

  const { data: orgs = [] } = useQuery({ queryKey: ["orgs"], queryFn: async () => (await api.get("/organizations")).data });
  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers", orgId, q],
    queryFn: async () => (await api.get("/customers", { params: { organization_id: orgId || undefined, q: q || undefined } })).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, organization_id: orgId || orgs[0]?.id };
      if (!body.organization_id) throw new Error("Create an organization first.");
      if (editId) return (await api.put(`/customers/${editId}`, body)).data;
      return (await api.post("/customers", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success(editId ? "Customer updated" : "Customer created");
      setOpenForm(false);
      setForm(emptyCustomer);
      setEditId(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/customers/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Customer deleted");
    },
  });

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({ ...emptyCustomer, ...c });
    setOrgId(c.organization_id);
    setOpenForm(true);
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Directory</div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter">Customers.</h1>
        </div>
        <button
          data-testid="new-customer-button"
          onClick={() => {
            setEditId(null);
            setForm(emptyCustomer);
            setOpenForm(true);
          }}
          className="inline-flex items-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-2.5 sm:px-5 sm:py-3 rounded-sm hover:bg-[#002FA7] hover:text-white transition-colors font-semibold self-start md:self-auto text-sm sm:text-base"
        >
          <Plus className="w-4 h-4" /> New Customer
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-3 py-2 rounded-sm">
          <Search className="w-4 h-4 text-slate-500" />
          <input
            data-testid="customer-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone, email…"
            className="w-full bg-transparent outline-none text-sm"
          />
        </div>
        <select
          data-testid="customer-org-filter"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          className="flex-1 md:flex-none border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm"
        >
          <option value="">All organizations</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.organization_name}
            </option>
          ))}
        </select>
      </div>

      <div className="border border-slate-200 dark:border-slate-800 rounded-sm bg-white dark:bg-[#121214] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr className="text-left uppercase text-[10px] tracking-[0.2em] text-slate-500">
              <th className="p-4">Name</th>
              <th className="p-4 hidden md:table-cell">Company</th>
              <th className="p-4 hidden md:table-cell">Phone</th>
              <th className="p-4 hidden lg:table-cell">Email</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!isLoading && customers.length === 0 && (
              <tr><td colSpan={5} className="p-12 text-center text-slate-500" data-testid="customers-empty">
                <Users className="w-6 h-6 mx-auto mb-3 text-slate-400" />
                No customers yet.
              </td></tr>
            )}
            {customers.map((c) => (
              <tr key={c.id} className="border-t border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900" data-testid={`customer-row-${c.id}`}>
                <td className="p-4 font-medium">{c.name}</td>
                <td className="p-4 hidden md:table-cell text-slate-500">{c.company || "—"}</td>
                <td className="p-4 hidden md:table-cell text-slate-500">{c.phone || "—"}</td>
                <td className="p-4 hidden lg:table-cell text-slate-500">{c.email || "—"}</td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => startEdit(c)} className="p-2 hover:text-[#002FA7]" data-testid={`edit-customer-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => window.confirm("Delete this customer?") && del.mutate(c.id)}
                      className="p-2 hover:text-red-600"
                      data-testid={`delete-customer-${c.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {openForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setOpenForm(false)}
          >
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-auto"
            >
              <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
                <h2 className="text-2xl font-extrabold tracking-tighter">{editId ? "Edit customer" : "New customer"}</h2>
                <button onClick={() => setOpenForm(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="md:col-span-2">
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Organization</span>
                  <select
                    data-testid="customer-form-org"
                    value={orgId}
                    onChange={(e) => setOrgId(e.target.value)}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm"
                  >
                    <option value="">Choose organization…</option>
                    {orgs.map((o) => (<option key={o.id} value={o.id}>{o.organization_name}</option>))}
                  </select>
                </label>
                {[["name","Customer Name *"],["company","Contact Person"],["phone","Phone"],["email","Email"],["gst","GST"],["city","City"],["state","State"],["state_code","State Code"],["country","Country"],["pincode","Pincode"]].map(([k,label]) => (
                  <label key={k}>
                    <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">{label}</span>
                    <input
                      data-testid={`customer-field-${k}`}
                      value={form[k] || ""}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                    />
                  </label>
                ))}
                <label className="md:col-span-2">
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Address</span>
                  <textarea
                    data-testid="customer-field-address"
                    rows={2}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                  />
                </label>
              </div>
              <div className="p-6 flex items-center justify-end gap-3 border-t border-slate-200 dark:border-slate-800">
                <button onClick={() => setOpenForm(false)} className="px-5 py-2.5 border border-slate-300 dark:border-slate-700 rounded-sm">Cancel</button>
                <button
                  data-testid="save-customer-button"
                  disabled={!form.name || save.isPending}
                  onClick={() => save.mutate()}
                  className="px-5 py-2.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73] disabled:opacity-50"
                >
                  {save.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
